import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import {
  Coffee,
  DollarSign,
  LogOut,
  Moon,
  Server,
  SquarePen,
  Sun,
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
} from "@/components/ui/sidebar";
import { useTheme } from "@/hooks/use-theme";
import { api, type ConversationListItem } from "@/lib/api";
import { ago, cn, stateDotClass, stateLabel } from "@/lib/states";

/**
 * The inset sidebar, kept quiet: ghost rows everywhere, the active ask as the
 * one filled element. Icon-collapse (⌘B or the rail) keeps the iconful
 * actions — new ask, fleet, costs, theme, sign out — as tooltipped icons and
 * folds the ask list away entirely (two-line text rows have no icon form).
 */

const FOOTER_NAV = [
  { to: "/fleet", label: "Fleet", icon: Server },
  { to: "/costs", label: "Costs", icon: DollarSign },
] as const;

function AskItem({
  conversation,
  activeId,
}: {
  conversation: ConversationListItem;
  activeId?: string;
}) {
  const task = conversation.tasks[0];
  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        isActive={conversation.id === activeId}
        className="h-auto flex-col items-start gap-0.5 py-1.5"
      >
        <Link
          to="/c/$conversationId"
          params={{ conversationId: conversation.id }}
        >
          <span className="w-full truncate">{conversation.title}</span>
          <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
            {task && (
              <>
                <span
                  className={cn(
                    "size-1.5 rounded-full",
                    stateDotClass(task.state),
                    (task.state === "running" || task.state === "preparing") &&
                      "animate-pulse",
                  )}
                />
                {stateLabel(task.state)}
                <span className="opacity-60">·</span>
              </>
            )}
            {ago(conversation.updatedAt)}
          </span>
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

export function AppSidebar({ activeId }: { activeId?: string }) {
  const navigate = useNavigate();
  const path = useRouterState({ select: (s) => s.location.pathname });
  const { resolved, setPreference } = useTheme();
  const conversations = useQuery({
    queryKey: ["conversations"],
    queryFn: () => api.conversations(),
    refetchInterval: 30_000, // SSE invalidation is the fast path; this is belt.
  });

  return (
    <Sidebar variant="inset" collapsible="icon">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton asChild size="lg" tooltip="Barista">
              <Link to="/">
                <Coffee className="text-brand" />
                <span className="font-semibold">Barista</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton
              tooltip="New ask"
              onClick={() => navigate({ to: "/" })}
            >
              <SquarePen />
              <span>New ask</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        {/* Two-line text rows have no icon form — the whole group folds away
            in icon mode instead of degrading into clipped text. */}
        <SidebarGroup className="group-data-[collapsible=icon]:hidden">
          <SidebarGroupLabel>Asks</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {conversations.data?.conversations.map((conversation) => (
                <AskItem
                  key={conversation.id}
                  conversation={conversation}
                  activeId={activeId}
                />
              ))}
              {conversations.data?.conversations.length === 0 && (
                <p className="px-2 py-1.5 text-muted-foreground text-xs">
                  No asks yet — start one with New ask, or mention @barista in
                  Slack.
                </p>
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          {FOOTER_NAV.map((item) => (
            <SidebarMenuItem key={item.to}>
              <SidebarMenuButton
                asChild
                tooltip={item.label}
                isActive={path.startsWith(item.to)}
              >
                <Link to={item.to}>
                  <item.icon />
                  <span>{item.label}</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          ))}
          <SidebarMenuItem>
            <SidebarMenuButton
              tooltip={resolved === "dark" ? "Light mode" : "Dark mode"}
              onClick={() =>
                setPreference(resolved === "dark" ? "light" : "dark")
              }
            >
              {resolved === "dark" ? <Sun /> : <Moon />}
              <span>{resolved === "dark" ? "Light mode" : "Dark mode"}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton asChild tooltip="Sign out">
              <a href="/auth/logout">
                <LogOut />
                <span>Sign out</span>
              </a>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
